import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Show, SignIn, useAuth } from "@clerk/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { OtpVerify } from "@/components/site/OtpVerify";
import { ComplianceNote } from "@/components/mlm/PackageCard";

const MAX = 10 * 1024 * 1024;
const FIELDS = [["full_name", "Full name", "sm:col-span-2"], ["mobile", "Mobile number", ""], ["address", "Full address", "sm:col-span-2"],
  ["district", "District", ""], ["state", "State", ""], ["pincode", "PIN code", ""]];

const FileField = ({ id, label, accept, hint, file, setFile }) => (
  <div className="space-y-1.5 rounded-xl border border-slate-200 p-4">
    <Label htmlFor={id}>{label} <span className="text-red-600">*</span></Label>
    <input id={id} type="file" accept={accept} className="block w-full text-sm" data-testid={`reg-${id}-input`}
      onChange={(e) => { const f = e.target.files[0]; if (f && f.size > MAX) { toast.error("File is larger than 10 MB. Please upload a file of 10 MB or less."); e.target.value = ""; return; } setFile(f || null); }} />
    <p className="text-xs text-slate-500">{file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : hint}</p>
  </div>
);

const SponsorCheck = ({ code }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    setInfo(null);
    if (code.trim().length < 6) return undefined;
    const t = setTimeout(() => api.get(`/mlm/sponsor/${encodeURIComponent(code.trim())}`).then((r) => setInfo(r.data)).catch(() => setInfo(false)), 400);
    return () => clearTimeout(t);
  }, [code]);
  if (info === null) return null;
  return info ? <p className="text-xs font-semibold text-emerald-700" data-testid="reg-sponsor-valid">Sponsor: {info.name} ({info.district})</p> : <p className="text-xs font-semibold text-red-600" data-testid="reg-sponsor-invalid">No active distributor with this ID</p>;
};

const RegisterForm = () => {
  const [params] = useSearchParams();
  const call = useAdminApi();
  const navigate = useNavigate();
  const [f, setF] = useState({ full_name: "", mobile: "", email: "", address: "", district: "", state: "", pincode: "", sponsor_code: params.get("ref") || localStorage.getItem("bmi_ref") || "" });
  const [token, setToken] = useState("");
  const [idProof, setIdProof] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(undefined);
  useEffect(() => {
    call("get", "/mlm/me").then((r) => setExisting(r.registration)).catch(() => setExisting(null));
  }, [call]);

  if (existing === undefined) return <p className="text-slate-500">Loading…</p>;
  if (existing && existing.status !== "rejected")
    return <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="reg-already"><p className="font-semibold">You have already registered (status: {existing.status}).</p><Link to="/member" className="mt-3 inline-block rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white">Go to dashboard</Link></div>;

  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("email_token", token);
    fd.append("accept_terms", accept);
    fd.append("id_proof", idProof);
    fd.append("photo", photo);
    try {
      await call("post", "/mlm/register", fd);
      toast.success("Registration submitted for verification");
      navigate("/member");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const ready = token && idProof && photo && accept && f.full_name && f.mobile && f.address && f.district && f.state && f.pincode;

  return (
    <div className="space-y-6" data-testid="distributor-register-form">
      <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
        {FIELDS.map(([k, label, span]) => (
          <div key={k} className={`space-y-1.5 ${span}`}><Label htmlFor={k}>{label}</Label><Input id={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`reg-${k}-input`} /></div>
        ))}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="email">Email address</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input id="email" type="email" className="max-w-sm" value={f.email} disabled={!!token} onChange={(e) => setF({ ...f, email: e.target.value.trim() })} data-testid="reg-email-input" />
            <OtpVerify channel="email" target={f.email} verified={!!token} onVerified={setToken} testid="reg-email-otp" />
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="sponsor">Sponsor referral ID (optional)</Label><Input id="sponsor" className="max-w-sm" placeholder="e.g. BMID000001" value={f.sponsor_code} onChange={(e) => setF({ ...f, sponsor_code: e.target.value.toUpperCase() })} data-testid="reg-sponsor-input" /><SponsorCheck code={f.sponsor_code} /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <FileField id="id_proof" label="Government ID proof" accept=".jpg,.jpeg,.png,.pdf" hint="Aadhaar, Voter ID or Driving Licence · JPG, PNG or PDF · max 10 MB" file={idProof} setFile={setIdProof} />
        <FileField id="photo" label="Your photograph" accept=".jpg,.jpeg,.png" hint="JPG or PNG · max 10 MB" file={photo} setFile={setPhoto} />
      </div>
      <p className="text-xs text-slate-500">Documents are stored privately and visible only to authorised BMI SP GROUP administrators.</p>
      <ComplianceNote />
      <label className="flex items-start gap-3 text-sm"><Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="reg-accept-terms" /><span>I accept the <Link to="/policies/distributor-terms" target="_blank" className="underline">Distributor Terms</Link>, <Link to="/policies/privacy" target="_blank" className="underline">Privacy Policy</Link> and <Link to="/policies/distributor-refund" target="_blank" className="underline">Refund & Cancellation Policy</Link>, and confirm my details are true.</span></label>
      <Button className="h-12 rounded-full bg-amber-600 px-8 text-base hover:bg-amber-700" disabled={!ready || busy} onClick={submit} data-testid="reg-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit registration</Button>
    </div>
  );
};

export default function DistributorRegister() {
  const { isLoaded } = useAuth();
  return (
    <div className="container-x max-w-4xl py-16">
      <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Distributor registration</p>
      <h1 className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">Join BMI SP GROUP</h1>
      <p className="mb-8 mt-3 text-slate-600">Free to join — no package purchase required. After admin verification you receive your unique Distributor ID and referral link, and can then order product packages from your dashboard.</p>
      {isLoaded && <Show when="signed-in"><RegisterForm /></Show>}
      {isLoaded && <Show when="signed-out"><div className="space-y-4" data-testid="reg-signin-required"><p className="font-semibold">First sign in or create your account.</p><SignIn routing="hash" withSignUp forceRedirectUrl={window.location.href} signUpForceRedirectUrl={window.location.href} /></div></Show>}
    </div>
  );
}
